import { useCallback, useEffect, useRef, useState } from 'react';

import { findDomOffsetMatches } from '../helpers/domSearchMatches';
import { PdfTextSearchMatch, searchPdfDocument } from '../helpers/pdfDocumentSearch';
import { clearSearchHighlightOverlay, paintSearchHighlightOverlay, scrollRangeIntoScrollContainer } from '../helpers/searchHighlightOverlay';

interface UsePdfViewerSearchArgs {
    pdfDocRef: React.RefObject<{ numPages: number; getPage: (n: number) => Promise<{ getTextContent: () => Promise<{ items: { str: string }[] }> }> } | null>;
    pdfReady: boolean;
    pageNumber: number;
    setPageNumber: (page: number) => void;
    textLayerRef: React.RefObject<HTMLDivElement | null>;
    searchOverlayRef: React.RefObject<HTMLDivElement | null>;
    scrollRef: React.RefObject<HTMLDivElement | null>;
    isPageRendering: boolean;
}

export function usePdfViewerSearch({
    pdfDocRef,
    pdfReady,
    pageNumber,
    setPageNumber,
    textLayerRef,
    searchOverlayRef,
    scrollRef,
    isPageRendering,
}: UsePdfViewerSearchArgs) {
    const [query, setQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [matches, setMatches] = useState<PdfTextSearchMatch[]>([]);
    const [matchIndex, setMatchIndex] = useState(0);
    const [searching, setSearching] = useState(false);
    const searchGenRef = useRef(0);
    const searchInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        const t = window.setTimeout(() => setDebouncedQuery(query), 280);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        const q = debouncedQuery.trim();
        if (!pdfReady || !pdfDocRef.current || !q) {
            setMatches([]);
            setMatchIndex(0);
            setSearching(false);
            clearSearchHighlightOverlay(searchOverlayRef.current);
            return;
        }

        const gen = ++searchGenRef.current;
        setSearching(true);

        void searchPdfDocument(pdfDocRef.current, q)
            .then((found) => {
                if (gen !== searchGenRef.current) return;
                setMatches(found);
                setMatchIndex(0);
                setSearching(false);
                if (found.length > 0 && found[0].pageNumber !== pageNumber) {
                    setPageNumber(found[0].pageNumber);
                }
            })
            .catch(() => {
                if (gen !== searchGenRef.current) return;
                setMatches([]);
                setMatchIndex(0);
                setSearching(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedQuery, pdfReady]);

    const activeMatch = matches.length > 0 ? matches[matchIndex] : null;
    const needle = debouncedQuery.trim();

    const repaint = useCallback(() => {
        const layer = textLayerRef.current;
        const overlay = searchOverlayRef.current;
        if (!layer || !overlay) return;

        if (!needle || matches.length === 0) {
            clearSearchHighlightOverlay(overlay);
            return;
        }

        const domOnPage = findDomOffsetMatches(layer, needle);
        const globalOnPage = matches
            .map((m, globalIndex) => ({ ...m, globalIndex }))
            .filter((m) => m.pageNumber === pageNumber);

        const activeLocalIndex = activeMatch && activeMatch.pageNumber === pageNumber
            ? globalOnPage.findIndex((m) => m.globalIndex === matchIndex)
            : -1;

        paintSearchHighlightOverlay(
            layer,
            overlay,
            domOnPage.map((m, localIndex) => ({
                startOffset: m.startOffset,
                endOffset: m.endOffset,
                active: localIndex === activeLocalIndex,
            })),
        );

        if (activeMatch && activeMatch.pageNumber === pageNumber && activeLocalIndex >= 0) {
            const domMatch = domOnPage[activeLocalIndex];
            if (domMatch) {
                scrollRangeIntoScrollContainer(
                    scrollRef.current,
                    layer,
                    domMatch.startOffset,
                    domMatch.endOffset,
                );
            }
        }
    }, [activeMatch, matchIndex, matches, needle, pageNumber, scrollRef, searchOverlayRef, textLayerRef]);

    useEffect(() => {
        if (isPageRendering) return;
        const t = window.setTimeout(repaint, 40);
        return () => clearTimeout(t);
    }, [isPageRendering, repaint]);

    const goToMatch = useCallback((index: number) => {
        if (matches.length === 0) return;
        const next = ((index % matches.length) + matches.length) % matches.length;
        setMatchIndex(next);
        const target = matches[next];
        if (target.pageNumber !== pageNumber) {
            setPageNumber(target.pageNumber);
        }
    }, [matches, pageNumber, setPageNumber]);

    const goNext = useCallback(() => goToMatch(matchIndex + 1), [goToMatch, matchIndex]);
    const goPrev = useCallback(() => goToMatch(matchIndex - 1), [goToMatch, matchIndex]);

    return {
        query,
        setQuery,
        searching,
        matchCount: matches.length,
        matchIndex,
        matchLabel: matches.length > 0 ? `${matchIndex + 1} / ${matches.length}` : '0 / 0',
        goNext,
        goPrev,
        searchInputRef,
    };
}
